import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from '../shared';
import { useToastContext } from '../../contexts/ToastContext';

const EventEndModal = ({ isOpen, onClose, onEnd, isLoading }) => {
  const { showToast } = useToastContext();
  const [notes, setNotes] = useState('');
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      setNotes('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setNotes('');
    onCloseRef.current();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const result = onEnd({
        notes: notes.trim() || null,
        screenshots: null,
      });

      if (result && typeof result.then === 'function') {
        await result;
      }
    } catch (error) {
      console.error('Error ending event:', error);
      showToast(
        error?.response?.data?.message || error?.message || 'Could not end event',
        'error'
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-xl font-bold text-gray-900">End Event</h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Ending the event will automatically update the booking status to
                &quot;Completed&quot;. If you don&apos;t end manually after the scheduled end time, the
                system can record an automatic end (same schedule as automatic start — refresh the page).
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                placeholder="Add any notes about ending the event..."
                maxLength={2000}
              />
              <p className="mt-1 text-xs text-gray-500">
                {notes.length}/2000 characters
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={isLoading}
                disabled={isLoading}
              >
                End Event
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventEndModal;
